import { createSlice } from '@reduxjs/toolkit';
import { withDealPricing, dealPricingFields } from '../utils/vendorDiscounts';

// Load cart from local storage
const loadCartFromLocalStorage = () => {
    const savedCart = localStorage.getItem('retailerWholesalerCart');
    return savedCart ? JSON.parse(savedCart) : {};
};

// Load total from local storage
const loadTotalFromLocalStorage = () => {
    const savedTotal = localStorage.getItem('retailerWholesalerTotal');
    return savedTotal ? JSON.parse(savedTotal) : 0;
};

// Save cart to local storage
const saveCartToLocalStorage = (carts) => {
    localStorage.setItem('retailerWholesalerCart', JSON.stringify(carts));
};

// Save total to local storage
const saveTotalToLocalStorage = (total) => {
    localStorage.setItem('retailerWholesalerTotal', JSON.stringify(total));
};

const calculateTotal = (carts) => {
    return Object.values(carts).reduce((total, vendorCart) => {
        return total + Object.values(vendorCart).reduce((vendorTotal, product) => {
            return vendorTotal + product.price * product.quantity;
        }, 0);
    }, 0);
};

const retailerWholesalerCartSlice = createSlice({
    name: 'retailerWholesalerCart',
    initialState: {
        carts: loadCartFromLocalStorage(),
        currentVendor: null,
        total: loadTotalFromLocalStorage(),
        shippingAddress: {},
        paymentMethod: null,
    },
    reducers: {
        addToCart: (state, action) => {
            const { vendorId, product, quantity } = action.payload;

            if (!state.carts[vendorId]) {
                state.carts[vendorId] = {};
            }
            // Vendor discounts: `price` on the line is salePrice ?? price, with
            // listPrice and the discount kept beside it.
            if (!state.carts[vendorId][product._id]) {
                state.carts[vendorId][product._id] = { ...withDealPricing(product), quantity: 0 };
            }
            Object.assign(state.carts[vendorId][product._id], dealPricingFields(product));
            state.carts[vendorId][product._id].quantity += quantity;
            state.currentVendor = vendorId;
            state.total = calculateTotal(state.carts);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        removeFromCart: (state, action) => {
            const { vendorId, productId } = action.payload;
            // const product = state.carts[vendorId][productId];
            delete state.carts[vendorId][productId];
            state.total = calculateTotal(state.carts);
            saveCartToLocalStorage(state.carts); // Save to local storage
            saveTotalToLocalStorage(state.total); // Save total to local storage
        },
        setCurrentVendor: (state, action) => {
            state.currentVendor = action.payload;
        },
        saveShippingAddress: (state, action) => {
            state.shippingAddress = action.payload;
        },
        savePaymentMethod: (state, action) => {
            state.paymentMethod = action.payload;
        },
        // ── One vendor = ONE cart ─────────────────────────────────────────
        // This is a MULTI-VENDOR cart: `carts` is keyed per vendor and must
        // stay that way. The bug this fixes is a KEY-IDENTITY one, not a
        // grouping one: the backend vendor lookup (`getRetailerWholesalerById`)
        // matches `{$or:[{_id},{owner}]}`, so a shop URL / vendor pointer can
        // carry EITHER the RetailerWholesaler entity `_id` (canonical) OR the
        // owner (Users) id (alias). Items added under each key split ONE
        // vendor into TWO cart groups — two subtotals, two checkouts, and a
        // double delivery fee. When a caller discovers the canonical id for
        // an alias key, this merges the alias cart into the canonical one
        // (quantities summed per product) and re-points `currentVendor`.
        mergeVendorCarts: (state, action) => {
            const { fromId, toId } = action.payload || {};
            if (!fromId || !toId || String(fromId) === String(toId)) return;
            const from = state.carts[fromId];
            if (from && Object.keys(from).length > 0) {
                if (!state.carts[toId]) state.carts[toId] = {};
                Object.entries(from).forEach(([productId, item]) => {
                    if (state.carts[toId][productId]) {
                        state.carts[toId][productId].quantity += item.quantity;
                    } else {
                        state.carts[toId][productId] = item;
                    }
                });
            }
            if (from) delete state.carts[fromId];
            if (state.currentVendor === fromId) state.currentVendor = toId;
            state.total = calculateTotal(state.carts);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        clearCart: (state, action) => {
            const vendorId = action.payload;
            state.carts[vendorId] = {};
            state.total = calculateTotal(state.carts);
            saveCartToLocalStorage(state.carts); // Save to local storage
            saveTotalToLocalStorage(state.total); // Save total to local storage
        },
        resetRetailerWholesalerCart: (state) => {
            state.carts = {};
            state.currentVendor = null;
            state.total = 0;
            state.shippingAddress = {};
            state.paymentMethod = null;
            saveCartToLocalStorage(state.carts); // Save to local storage
            saveTotalToLocalStorage(state.total); // Save total to local storage
        },
    },
});

export const { addToCart, removeFromCart, setCurrentVendor, saveShippingAddress, savePaymentMethod, mergeVendorCarts, clearCart, resetRetailerWholesalerCart, confirmVendorSwitch, cancelVendorSwitch } = retailerWholesalerCartSlice.actions;
export default retailerWholesalerCartSlice.reducer;
